import React, { useCallback, useEffect, useState } from 'react';
import { Input, Button, Row, Col, Table, Form } from 'reactstrap';
import { useClienteSearch } from '../FilterClientScreen/useClientSearch';
import { useForm, Controller } from 'react-hook-form';
import Pagination from '../../../../components/Pagination';
import { TableContainer } from '../FilterClientScreen/styled';
import ReactLoader from 'components/Loader';
import AccountService from '../../../../../src/views/CRM/Account/services/accountService';

const availableSearchTypes = {
  INPUT_TEXT: 'INPUT_TEXT',
  SELECT_BOX: 'SELECT_BOX',
  INPUT_NUMBER: 'INPUT_NUMBER'
};

const FilterClientBroadcastList = ({
  setSelectedClient,
  account = false,
  selectCurrent = []
}) => {
  const { control, getValues, setValue } = useForm();
  const { fieldsConfig, searchClient, loading } = useClienteSearch({ account });
  const [clients, setClients] = useState([]);
  const [activePage, setActivePage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [searchType, setSearchType] = useState(availableSearchTypes.INPUT_TEXT);
  const [currentFieldsToSelectBox, setCurrentFieldsToSelectBox] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);

  const lastAttributeAtClientSearch = localStorage.getItem(
    'flexuc:lastAttributeAtClientSearch'
  );

  useEffect(() => {
    if (selectCurrent) {
      setSelectedClients(selectCurrent);
    }
  }, []);

  const fieldNameIsNumericType = useCallback(
    (fieldName) => {
      return (
        fieldsConfig?.filter((field) => field.name === fieldName)?.type ===
        'NUMERIC'
      );
    },
    [fieldsConfig]
  );

  const handleInputKeyChange = useCallback(
    (fieldName) => {
      localStorage.setItem('flexuc:lastAttributeAtClientSearch', fieldName);

      const isNumberField = fieldNameIsNumericType(fieldName);
      const isSelectBoxField =
        fieldsConfig.find((field) => field.name === fieldName)?.type === 'LIST';

      if (isSelectBoxField && !isNumberField) {
        setSearchType(availableSearchTypes.SELECT_BOX);
        const options = fieldsConfig.filter(
          (field) => field.name === fieldName
        );

        const newOptions =
          (options &&
            !!options.length &&
            options[0].options.map((op) => op.value)) ||
          [];
        return setCurrentFieldsToSelectBox([...newOptions]);
      } else if (!isSelectBoxField && !isNumberField) {
        setSearchType(availableSearchTypes.INPUT_TEXT);
        setValue('searchValue', '');
        return setCurrentFieldsToSelectBox([]);
      } else {
        setSearchType(availableSearchTypes.INPUT_NUMBER);
        setValue('searchValue', 0);
        return setCurrentFieldsToSelectBox([]);
      }
    },
    [fieldsConfig, setValue, fieldNameIsNumericType]
  );

  useEffect(() => {
    if (!fieldsConfig?.length || !lastAttributeAtClientSearch) return;

    setValue('searchKey', lastAttributeAtClientSearch);
    handleInputKeyChange(lastAttributeAtClientSearch);
  }, [
    fieldsConfig,
    handleInputKeyChange,
    lastAttributeAtClientSearch,
    setValue
  ]);

  const getCustomFieldsNameAndLabel = () => {
    if (!fieldsConfig?.length) return [];

    return fieldsConfig
      .filter((field) =>
        ['TEXT', 'NUMERIC', 'LIST', 'CPF', 'CNPJ'].includes(field.type)
      )
      .map((field) => ({ name: field.name, label: field.label }));
  };

  const onSearch = useCallback(
    async (page = 1) => {
      const { searchKey, searchValue } = getValues();

      if (!searchKey || !searchValue) {
        return setClients([]);
      }
      let resultSet = [];
      if (account) {
        resultSet = await AccountService.getAllAccounts(
          {
            [searchKey]: searchValue
          },
          {
            page,
            limit: 10
          }
        );
      } else {
        resultSet = await searchClient({
          attribute: searchKey,
          value: searchValue,
          page
        });
      }

      const total =
        (resultSet && resultSet.quantity && Number(resultSet.quantity)) ||
        (resultSet &&
          resultSet.data &&
          Number(resultSet.data.totalRegisters)) ||
        0;
      setTotalItems(total);

      setClients(resultSet?.content || resultSet?.data?.data || []);
    },
    [getValues, searchClient]
  );

  useEffect(() => {
    onSearch(activePage);
  }, [activePage, onSearch]);

  const handlePageChange = (newPage) => {
    setActivePage(newPage);
  };

  const onSubmit = (event) => {
    event.preventDefault();
    onSearch();
  };

  const handleSelectAll = (isChecked) => {
    if (isChecked) {
      const visibleClients = clients.map((client) => ({
        id: client.id,
        name: client.nome || client.name
      }));
      setSelectedClients((prevSelected) => {
        const newSelection = [...prevSelected, ...visibleClients];
        return newSelection.filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.id === item.id)
        );
      });
    } else {
      const visibleClientIds = clients.map((client) => client.id);
      setSelectedClients((prevSelected) =>
        prevSelected.filter((item) => !visibleClientIds.includes(item.id))
      );
    }
  };

  const handleSelectIndividual = (clientId, clientName, checked) => {
    setSelectedClients((prevSelected) => {
      if (checked) {
        const newClient = { id: clientId, name: clientName };
        return [...prevSelected, newClient];
      } else {
        return prevSelected.filter((item) => item.id !== clientId);
      }
    });
  };

  const handleSaveAndClose = () => {
    if (selectedClients.length > 0) {
      selectedClients.forEach((client) => {
        setSelectedClient({
          clientId: client.id,
          clientName: client.nome || client.name,
          clientPhone: client.telefone || client.telephone,
          clientEmail: client.email
        });
      });
    }
  };

  return (
    <>
      <div className="mb-2">
        <Form name="formSearchClient" onSubmit={onSubmit}>
          <Row>
            <Col sm="3" className="my-1">
              <Controller
                as={
                  <Input
                    type="select"
                    name="searchKey"
                    onChangeCapture={(event) =>
                      handleInputKeyChange(event.target.value)
                    }
                  >
                    <option value="">Buscar por ...</option>
                    <option value={account ? 'name' : 'nome'}>Nome</option>
                    {!account && <option value="email">E-mail</option>}
                    <option value="accountName">Conta</option>
                    <option value={account ? 'telephone' : 'telefone'}>
                      Telefone
                    </option>
                    {Object.keys(fieldsConfig).length &&
                      fieldsConfig
                        .filter((field) =>
                          ['TEXT', 'NUMERIC', 'LIST', 'CPF', 'CNPJ'].includes(
                            field.type
                          )
                        )
                        .map((field) => (
                          <option key={field.name} value={field.name}>
                            {field.label}
                          </option>
                        ))}
                  </Input>
                }
                control={control}
                name="searchKey"
                defaultValue=""
              />
            </Col>
            <Col sm="7" className="my-1">
              {searchType === availableSearchTypes.INPUT_TEXT && (
                <Controller
                  as={Input}
                  control={control}
                  defaultValue={''}
                  name="searchValue"
                />
              )}
              {searchType === availableSearchTypes.SELECT_BOX && (
                <Controller
                  as={
                    <Input type="select" name="searchValue">
                      <option value="">Selecione</option>
                      {currentFieldsToSelectBox.map((fieldName, index) => (
                        <option key={index} value={fieldName}>
                          {fieldName}
                        </option>
                      ))}
                    </Input>
                  }
                  control={control}
                  name="searchValue"
                  defaultValue=""
                />
              )}
              {searchType === availableSearchTypes.INPUT_NUMBER && (
                <Controller
                  as={Input}
                  type="number"
                  control={control}
                  defaultValue={0}
                  name="searchValue"
                />
              )}
            </Col>

            <Col sm="2" className="my-1">
              <Button color="info" type="submit">
                Buscar
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
      <div id="listClientsTable">
        <TableContainer
          style={{
            overflowX: 'auto'
          }}
        >
          {loading ? (
            <Row className="justify-content-md-center py-3">
              <ReactLoader />
            </Row>
          ) : (
            <Table hover={true}>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </th>
                  <th>Nome</th>
                  {!account && <th>Email</th>}
                  <th>Telefone</th>
                  <th>Conta</th>
                  {getCustomFieldsNameAndLabel().map((field) => (
                    <th key={field.name}>{field.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {clients.length
                  ? clients.map((client) => (
                      <tr key={client.id} style={{ cursor: 'pointer' }}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedClients.some(
                              (selected) => selected.id === client.id
                            )}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleSelectIndividual(
                                client.id,
                                client.nome || client.name,
                                e.target.checked
                              );
                            }}
                          />
                        </td>
                        <td>{client.nome || client.name}</td>
                        {!account && <td>{client.email}</td>}
                        <td>{client.telefone || client.telephone}</td>
                        {getCustomFieldsNameAndLabel().map((field) => (
                          <td key={field.name}>{client[field.name]}</td>
                        ))}
                      </tr>
                    ))
                  : null}
              </tbody>
            </Table>
          )}
        </TableContainer>
        {clients.length > 0 && (
          <Pagination
            totalItems={totalItems}
            activePage={activePage}
            itemsPerPage={10}
            handlePageChange={handlePageChange}
          />
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        {selectedClients.length > 0 && (
          <Button
            color="success"
            onClick={handleSaveAndClose}
            className="btn-block"
            style={{ marginTop: '1rem', width: '20%' }}
          >
            Salvar
          </Button>
        )}
      </div>
    </>
  );
};

export default FilterClientBroadcastList;
